import { useCallback, useEffect, useMemo, useState } from "react";
import {
  IoCheckmarkDoneOutline,
  IoDownloadOutline,
  IoEyeOutline,
  IoTrashOutline,
} from "react-icons/io5";
import {
  deleteQuoteRequest,
  listQuoteRequests,
  markQuoteRequestRead,
} from "../../../api/adminQuoteRequestsApi";
import { getApiErrorMessage } from "../../../api/unwrapApiResponse";
import ConfirmDialog from "../../../components/dashboard/ConfirmDialog";
import EmptyState from "../../../components/dashboard/EmptyState";
import ErrorState from "../../../components/dashboard/ErrorState";
import LoadingState from "../../../components/dashboard/LoadingState";
import { useNotify } from "../../../components/ui/NotificationProvider";
import { getApiBaseUrl } from "../../../config/apiBase";

const SERVICE_LABELS = {
  discovery: "Discovery & strategy",
  development: "Custom development",
  design: "Product & UX design",
  data: "Data & analytics",
  infra: "Cloud & infrastructure",
  other: "Other",
};

const BUDGET_LABELS = {
  "under-10": "Under $10,000",
  "10-50": "$10,000 – $50,000",
  "50-150": "$50,000 – $150,000",
  "150-plus": "$150,000+",
  unsure: "Not sure yet",
};

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function resolveFileUrl(path) {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const base = getApiBaseUrl().replace(/\/$/, "");
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
}

function normalizeAttachments(request) {
  const raw = request?.attachments ?? request?.Attachments ?? [];
  if (!Array.isArray(raw)) return [];

  return raw
    .map((file) => ({
      url: file?.url ?? file?.Url ?? "",
      fileName: file?.fileName ?? file?.FileName ?? "Attachment",
    }))
    .filter((file) => file.url);
}

function labelFor(map, value) {
  return map[value] || value || "—";
}

function QuoteRequestDetailModal({ request, busy, onClose, onMarkRead, onDelete }) {
  if (!request) return null;

  const attachments = normalizeAttachments(request);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-slate-900/40"
        onClick={onClose}
      />
      <div className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-gray-200 bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">
              {request.firstName} {request.lastName}
            </h2>
            <p className="mt-1 text-sm text-slate-500">Quote request</p>
          </div>
          {!request.isRead ? (
            <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
              New
            </span>
          ) : null}
        </div>

        <dl className="mt-6 grid gap-4 text-sm sm:grid-cols-2">
          <div>
            <dt className="font-medium text-slate-500">Email</dt>
            <dd className="mt-1 text-slate-900">{request.email}</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Phone</dt>
            <dd className="mt-1 text-slate-900">{request.phone || "—"}</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Company</dt>
            <dd className="mt-1 text-slate-900">{request.company || "—"}</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Service</dt>
            <dd className="mt-1 text-slate-900">
              {labelFor(SERVICE_LABELS, request.service)}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Budget</dt>
            <dd className="mt-1 text-slate-900">
              {labelFor(BUDGET_LABELS, request.budget)}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Submitted</dt>
            <dd className="mt-1 text-slate-900">{formatDate(request.createdAt)}</dd>
          </div>
        </dl>

        {request.description ? (
          <div className="mt-6">
            <p className="text-sm font-medium text-slate-500">Project description</p>
            <p className="mt-2 whitespace-pre-wrap rounded-xl bg-slate-50 p-4 text-sm text-slate-800">
              {request.description}
            </p>
          </div>
        ) : null}

        {attachments.length ? (
          <div className="mt-6">
            <p className="mb-2 text-sm font-medium text-slate-500">
              Attachments ({attachments.length})
            </p>
            <ul className="space-y-2">
              {attachments.map((file, index) => (
                <li key={`${file.url}-${index}`}>
                  <a
                    href={resolveFileUrl(file.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex w-full items-center gap-2 rounded-xl border border-gray-200 px-3 py-2 text-sm text-slate-700 hover:bg-gray-50"
                  >
                    <IoDownloadOutline className="h-4 w-4 shrink-0" />
                    <span className="truncate">{file.fileName || "Download"}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="mt-6 flex flex-wrap gap-2">
          {!request.isRead ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => onMarkRead(request, true)}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              <IoCheckmarkDoneOutline className="h-4 w-4" />
              Mark as read
            </button>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={() => onMarkRead(request, false)}
              className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-slate-700 disabled:opacity-50"
            >
              Mark as unread
            </button>
          )}

          <button
            type="button"
            disabled={busy}
            onClick={() => onDelete(request)}
            className="inline-flex items-center gap-2 rounded-xl border border-red-200 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
          >
            <IoTrashOutline className="h-4 w-4" />
            Delete
          </button>

          <button
            type="button"
            onClick={onClose}
            className="ml-auto rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-slate-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function QuoteRequestsManager() {
  const notify = useNotify();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listQuoteRequests();
      setRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return requests.filter((item) => {
      if (filter === "unread" && item.isRead) return false;
      if (filter === "read" && !item.isRead) return false;
      if (!q) return true;

      const haystack = [
        item.firstName,
        item.lastName,
        item.email,
        item.phone,
        item.company,
        item.service,
        item.budget,
        item.description,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [requests, query, filter]);

  async function handleMarkRead(request, isRead) {
    setBusy(true);
    try {
      const updated = await markQuoteRequestRead(request.id, isRead);
      setRequests((prev) =>
        prev.map((item) => (item.id === updated.id ? updated : item))
      );
      setSelected((prev) => (prev?.id === updated.id ? updated : prev));
      notify.success(isRead ? "Marked as read" : "Marked as unread");
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function openRequest(request) {
    setSelected(request);
    if (!request.isRead) {
      try {
        const updated = await markQuoteRequestRead(request.id, true);
        setRequests((prev) =>
          prev.map((item) => (item.id === updated.id ? updated : item))
        );
        setSelected(updated);
      } catch {
        // Keep modal open even if mark-read fails.
      }
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await deleteQuoteRequest(deleteTarget.id);
      setRequests((prev) => prev.filter((item) => item.id !== deleteTarget.id));
      if (selected?.id === deleteTarget.id) setSelected(null);
      notify.success("Quote request deleted");
      setDeleteTarget(null);
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <LoadingState message="Loading quote requests…" />;
  if (error) return <ErrorState message={error} onRetry={loadRequests} />;

  const unreadCount = requests.filter((r) => !r.isRead).length;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-slate-900">Quote requests</h2>
        <p className="mt-1 text-sm text-slate-500">
          {requests.length} total
          {unreadCount > 0 ? ` · ${unreadCount} unread` : ""}
        </p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, email, company, or service…"
          className="w-full max-w-md rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
        />

        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-400"
        >
          <option value="all">All</option>
          <option value="unread">Unread</option>
          <option value="read">Read</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title={query || filter !== "all" ? "No matching requests" : "No quote requests yet"}
          description={
            query || filter !== "all"
              ? "Try a different search or filter."
              : "Quote requests submitted from the website will appear here."
          }
        />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600">
                  Requester
                </th>
                <th className="hidden px-4 py-3 text-left font-medium text-slate-600 md:table-cell">
                  Service
                </th>
                <th className="hidden px-4 py-3 text-left font-medium text-slate-600 lg:table-cell">
                  Budget
                </th>
                <th className="hidden px-4 py-3 text-left font-medium text-slate-600 md:table-cell">
                  Files
                </th>
                <th className="hidden px-4 py-3 text-left font-medium text-slate-600 sm:table-cell">
                  Submitted
                </th>
                <th className="px-4 py-3 text-right font-medium text-slate-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((item) => {
                const attachmentCount = normalizeAttachments(item).length;

                return (
                <tr
                  key={item.id}
                  className={`hover:bg-slate-50/80 ${!item.isRead ? "bg-emerald-50/30" : ""}`}
                >
                  <td className="px-4 py-3">
                    <div className="min-w-0">
                      <p className="truncate font-medium text-slate-900">
                        {item.firstName} {item.lastName}
                        {!item.isRead ? (
                          <span className="ml-2 inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                            New
                          </span>
                        ) : null}
                      </p>
                      <p className="truncate text-xs text-slate-500">{item.email}</p>
                    </div>
                  </td>
                  <td className="hidden px-4 py-3 text-slate-600 md:table-cell">
                    {labelFor(SERVICE_LABELS, item.service)}
                  </td>
                  <td className="hidden px-4 py-3 text-slate-600 lg:table-cell">
                    {labelFor(BUDGET_LABELS, item.budget)}
                  </td>
                  <td className="hidden px-4 py-3 text-slate-600 md:table-cell">
                    {attachmentCount
                      ? `${attachmentCount} file${attachmentCount === 1 ? "" : "s"}`
                      : "—"}
                  </td>
                  <td className="hidden px-4 py-3 text-slate-600 sm:table-cell">
                    {formatDate(item.createdAt)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => openRequest(item)}
                        className="rounded-lg border border-gray-200 p-2 text-slate-600 hover:bg-gray-50"
                        aria-label={`View quote request from ${item.firstName} ${item.lastName}`}
                      >
                        <IoEyeOutline className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(item)}
                        className="rounded-lg border border-gray-200 p-2 text-red-600 hover:bg-red-50"
                        aria-label={`Delete quote request from ${item.firstName} ${item.lastName}`}
                      >
                        <IoTrashOutline className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <QuoteRequestDetailModal
        request={selected}
        busy={busy}
        onClose={() => setSelected(null)}
        onMarkRead={handleMarkRead}
        onDelete={(item) => {
          setSelected(null);
          setDeleteTarget(item);
        }}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete quote request?"
        message={
          deleteTarget
            ? `This will permanently delete the quote request from ${deleteTarget.firstName} ${deleteTarget.lastName}.`
            : ""
        }
        busy={busy}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
